import * as React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Button,
  Alert,
  Snackbar,
} from "@mui/material";
import { useI18n } from "../../i18n/I18nProvider";
import { useWorkshopBlocker, FLOW } from "../../logic/useWorkshopBlocker";
import { supabase } from "../../logic/client";


export default function OthersSaid(props: {handlePageChange: (page: string) => void}) {
  const { handlePageChange } = props;
  const blocker = useWorkshopBlocker();
  const { lang } = useI18n();
  const isDE = lang === "de";
  const [notice, setNotice] = React.useState<string | null>(null);

  const [topThreats, setTopThreats] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  /* ---------------- NAVIGATION ---------------- */

  const myIndex = FLOW.indexOf("OthersSaid");
  const blockerIndex = FLOW.indexOf(blocker);
  const canContinue = blockerIndex > myIndex;
  
  function handleContinue() {
    if (canContinue) {
      handlePageChange(FLOW[myIndex + 1]);
      return;
    }
    setNotice("ENG / DE");
  }

  /* ---------------- LOAD DATA ---------------- */

  React.useEffect(() => {
    loadTopThreats();
  }, [lang]);

  async function loadTopThreats() {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("workshop_participant_threats")
        .select(`
          threat_id,
          threats (
            label_en,
            label_de
          )
        `);

      if (error) {
        console.error("LOAD ERROR:", error);
        return;
      }

      if (!data) return;

      // 🧠 AGGREGATE
      const counts: Record<string, any> = {};

      data.forEach((row: any) => {
        const id = row.threat_id;

        if (!counts[id]) {
          counts[id] = {
            label_en: row.threats?.label_en,
            label_de: row.threats?.label_de,
            count: 0,
          };
        }

        counts[id].count++;
      });

      // 🔥 SORT + TOP 10
      const sorted = Object.values(counts)
        .sort((a: any, b: any) => b.count - a.count)
        .slice(0, 10);

      setTopThreats(sorted);

    } catch (err) {
      console.error("AGG ERROR:", err);
    } finally {
      setLoading(false);
    }
  }

  /* ---------------- UI ---------------- */

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 48px)",
        width: "100%",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
        backgroundImage: `
          linear-gradient(
            rgba(8, 8, 8, 0.78),
            rgba(8, 8, 8, 0.82)
          ),
          url("/scenarioBgs/threats.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <Paper
        sx={{
          width: { xs: "100%", sm: "min(88vw, 780px)", md: "min(78vw, 860px)" },
          borderRadius: 5,
          p: { xs: 4, sm: 6, md: 7 },
          border: "1px solid rgba(255,255,255,0.14)",
          background:
            "linear-gradient(180deg, rgba(49, 32, 32, 0.95), rgba(34, 21, 21, 0.94))",
          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
          textAlign: "center",
        }}
      >
        <Stack spacing={4} alignItems="center">

          {/* TITLE */}
          <Typography
            sx={{
              color: "#fff",
              fontSize: { xs: "1.6rem", sm: "2rem", md: "2.3rem" },
              fontWeight: 800,
              letterSpacing: "0.08em",
              lineHeight: 1.3,
            }}
          >
            {isDE
              ? "Hier siehst du, was andere gesagt haben"
              : "Here you can see what others said"}
          </Typography>

          {/* SUBTEXT */}
          <Typography
            sx={{
              color: "rgba(255,255,255,0.8)",
              fontSize: { xs: "1rem", sm: "1.15rem" },
            }}
          >
            {isDE
              ? "Deine Antwort ist entscheidend – denn aus individueller Resilienz entsteht gesellschaftliche Resilienz. Und daraus entstehen Abschreckung und Frieden."
              : "Your answer matters – because individual resilience builds societal resilience. And from that, deterrence and peace emerge."}
          </Typography>

          {/* LIST */}
          <Stack spacing={1.5} sx={{ width: "100%", mt: 3 }}>
            {loading && (
              <Typography sx={{ color: "#fff" }}>
                {isDE ? "Lade Daten..." : "Loading data..."}
              </Typography>
            )}

            {!loading && topThreats.map((t, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Typography sx={{ color: "#fff" }}>
                  {i + 1}. {isDE ? t.label_de : t.label_en}
                </Typography>

                <Typography sx={{ color: "#ff5a5a", fontWeight: 700 }}>
                  {t.count}
                </Typography>
              </Box>
            ))}

            <Button
              variant="contained"
              onClick={handleContinue}
              sx={{
                mt: 1,
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 800,
                px: 3,
                bgcolor: canContinue ? "#ffffff" : "rgba(255,255,255,0.35)",
                color: canContinue ? "#111827" : "rgba(255,255,255,0.8)",
                "&:hover": {
                  bgcolor: canContinue ? "#f3f4f6" : "rgba(255,255,255,0.35)",
                },
              }}
            >
              {isDE ? "Weiter" : "Continue"}
            </Button>
          </Stack>

        </Stack>
      </Paper>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setNotice(null)} severity="info" sx={{ width: "100%", fontWeight: 700 }}>
          {isDE ? "Die Workshop-Leitung wird diesen Schritt freigeben, oder bitte beantworte die Frage vollständig und sende sie zuerst ab." : "The workshop holder will enable this step, or please complete and submit your answer first."}
        </Alert>
      </Snackbar>
    </Box>
  );
}